import React, { useMemo, useState, useEffect } from "react";
import { gql, useQuery } from "urql";
import { Link, useSearchParams, useLocation } from "wouter";
import { sortBy } from "lodash";
import { Dropdown } from "../../../components/src/Dropdown";

import { Instrument, InstrumentFilterInput, InstrumentStatusEnum } from "../types.generated";
import { ReactTable } from "../../../components/src/react-table/ReactTable";
import {
    SelectColumnFilter,
    SelectColumnFilterArrayOfIds,
    SelectColumnFilterType
} from "../../../components/src/react-table/ReactTableFilters";

const GET_INSTRUMENTS = gql`
    query instruments($filter: InstrumentFilterInput) {
        instruments(filter: $filter) {
            _id
            clientIds
            clients {
                _id
                name
            }
            issuerId
            issuer {
                name
            }
            issuerProgramId
            issuerProgram {
                name
            }
            name
            isin
            productType
            type
            modelType
            longName
            bloombergTicker
            currency
            bloombergDailyDownload
            updateTimestamp
            status
        }
    }
`;

export const InstrumentsPage = (): React.ReactElement => {
    const [queryArgs] = useSearchParams();
    const [_, navigate] = useLocation();

    const [variables, setVariables] = useState(null);

    const [{ fetching, data, error }] = useQuery<{ instruments?: Partial<Instrument>[] }>({
        query: GET_INSTRUMENTS,
        variables: variables,
        requestPolicy: "cache-and-network"
    });

    useEffect(() => {
        let filter: InstrumentFilterInput = {};
        if (queryArgs.get("issuerProgramIdIn") !== null) {
            filter = { ...filter, issuerProgramIdIn: queryArgs.get("issuerProgramIdIn")?.split(",") };
        }

        setVariables({ filter });
    }, [queryArgs]);

    const instruments = useMemo(() => {
        if (data && data.instruments) {
            return sortBy(data.instruments, "updateTimestamp").reverse();
        }
        return [];
    }, [data]);

    const columns = useMemo(() => {
        const clientsById: Record<string, { _id: string; name: string }> = {};

        instruments.forEach((instrument: Partial<Instrument>) => {
            instrument.clients.forEach((client) => {
                if (!(client._id in clientsById)) {
                    clientsById[client._id] = client;
                }
            });
        });

        return [
            {
                header: "Id",
                accessorKey: "_id",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/instruments/${row.original._id}`}>{cellProps.getValue()}</Link>;
                },
                size: 90
            },
            {
                header: "Clients",
                accessorKey: "clientIds",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    if (value) {
                        return (
                            <div className="w-full">
                                {value.map((id) => {
                                    if (clientsById[id]) {
                                        return (
                                            <Link key={id} to={`/parties/${id}`}>
                                                {clientsById[id].name}
                                            </Link>
                                        );
                                    }
                                })}
                            </div>
                        );
                    } else {
                        return null;
                    }
                },
                filter: SelectColumnFilterArrayOfIds(Object.values(clientsById)),
                size: 100
            },
            {
                header: "Issuer",
                id: "issuer.name",
                accessorKey: "issuer.name",
                accessorFn: (row) => (row.issuer ? row.issuer.name : null),
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.issuer) {
                        return <Link to={`/parties/${row.original.issuerId}`}>{cellProps.getValue()}</Link>;
                    } else {
                        return null;
                    }
                },
                size: 100
            },
            {
                header: "Issuer program",
                id: "issuerProgram.name",
                accessorKey: "issuerProgram.name",
                accessorFn: (row) => (row.issuerProgram ? row.issuerProgram.name : null),
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.issuerProgram) {
                        return <Link to={`/issuerprograms/${row.original.issuerProgramId}`}>{cellProps.getValue()}</Link>;
                    } else {
                        return null;
                    }
                },
                size: 100
            },
            {
                header: "Name",
                accessorKey: "name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/instruments/${row.original._id}`}>{cellProps.getValue()}</Link>;
                },
                size: 120
            },
            {
                header: "Isin",
                accessorKey: "isin",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/instruments/${row.original._id}`}>{cellProps.getValue()}</Link>;
                }
            },
            {
                header: "Long name",
                accessorKey: "longName"
            },
            {
                header: "Currency",
                accessorKey: "currency",
                filter: SelectColumnFilter,
                size: 75
            },
            {
                header: "Bloomberg ticker",
                accessorKey: "bloombergTicker",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/instruments/${row.original._id}`}>{cellProps.getValue()}</Link>;
                }
            },
            {
                header: "Product type",
                accessorKey: "productType",
                filter: SelectColumnFilter
            },
            {
                header: "Type",
                accessorKey: "type",
                filter: SelectColumnFilter
            },
            {
                header: "Model type",
                accessorKey: "modelType",
                filter: SelectColumnFilter
            },
            {
                header: "Status",
                accessorKey: "status",
                filter: SelectColumnFilterType(InstrumentStatusEnum)
            },
            {
                header: "Update timestamp",
                accessorKey: "updateTimestamp",
                size: 150
            }
        ];
    }, [instruments]);

    if (fetching) return <div>Loading...</div>;

    if (error)
        return (
            <div>
                <p>Error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    if (instruments.length === 0) return <div>No instruments</div>;
    return (
        <div>
            <div className="flex">
                <div className="ml-auto p-2">
                    <Dropdown align="end">
                        <Dropdown.Toggle variant="primary" size="sm" id="new-instrument-dropdown">
                            New
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item as="button" onClick={() => navigate("/instruments/new")}>
                                Blank
                            </Dropdown.Item>
                            <Dropdown.Item as="button" onClick={() => navigate("/instruments/new/ZeroCouponLinear")}>
                                ZeroCouponLinear
                            </Dropdown.Item>
                            <Dropdown.Item as="button" onClick={() => navigate("/instruments/new/Swaption")}>
                                Swaption
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>
            <p>Number of instruments: {instruments.length}</p>

            <ReactTable
                columns={columns}
                data={instruments}
                defaultHiddenColumns={["issuer.name", "issuerProgram.name", "status"]}
                exportToXlsxFile="instruments.xlsx"
            />
        </div>
    );
};
